import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d";
import { useMcpSchema, type TableSchema } from "@/hooks/useMcpSchema";
import { Loader2, RefreshCw, ZoomIn, ZoomOut, Maximize2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

interface GraphNode {
  id: string;
  label: string;
  columns: { name: string; type: string }[];
  val: number; // node size
  color: string;
  x?: number;
  y?: number;
}

interface GraphLink {
  source: string;
  target: string;
  label: string;
  sourceColumn: string;
  targetColumn: string;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const NODE_COLORS = [
  "#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899",
  "#f43f5e", "#ef4444", "#f97316", "#eab308", "#84cc16",
  "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6", "#2563eb",
];

function getNodeColor(index: number): string {
  return NODE_COLORS[index % NODE_COLORS.length];
}

/** Infer relationships from column names ending in _id. */
function inferRelationships(tables: TableSchema[]): GraphLink[] {
  const tableNames = new Set(tables.map((t) => t.table_name));
  const links: GraphLink[] = [];
  const seen = new Set<string>();

  for (const table of tables) {
    for (const col of table.columns) {
      const name = col.column_name;
      if (!name.endsWith("_id") || name === "id") continue;

      // Try to find the referenced table
      const base = name.slice(0, -3); // strip _id
      const candidates = [
        base,
        base + "s",
        base + "es",
        base.replace(/ie$/, "y") + "s",
        base.replace(/([^s])$/, "$1s"),
      ];

      for (const candidate of candidates) {
        if (tableNames.has(candidate) && candidate !== table.table_name) {
          const key = [table.table_name, candidate, name].sort().join("::");
          if (!seen.has(key)) {
            seen.add(key);
            links.push({
              source: table.table_name,
              target: candidate,
              label: name,
              sourceColumn: name,
              targetColumn: "id",
            });
          }
          break;
        }
      }
    }
  }

  return links;
}

function buildGraphData(tables: TableSchema[]): GraphData {
  const links = inferRelationships(tables);

  // Track which tables are connected
  const connectedTables = new Set<string>();
  for (const link of links) {
    connectedTables.add(link.source);
    connectedTables.add(link.target);
  }

  const nodes: GraphNode[] = tables.map((t, i) => ({
    id: t.table_name,
    label: t.table_name,
    columns: t.columns.map((c) => ({ name: c.column_name, type: c.data_type })),
    val: Math.max(3, Math.min(10, t.columns.length / 2)),
    color: getNodeColor(i),
  }));

  return { nodes, links };
}

// ── Detail Panel ─────────────────────────────────────────────────────────────

const DetailPanel = ({
  node,
  links,
  onClose,
}: {
  node: GraphNode;
  links: GraphLink[];
  onClose: () => void;
}) => {
  const relatedLinks = links.filter(
    (l) =>
      (typeof l.source === "string" ? l.source : (l.source as any).id) === node.id ||
      (typeof l.target === "string" ? l.target : (l.target as any).id) === node.id,
  );

  return (
    <div className="absolute right-4 top-4 z-20 w-80 max-h-[calc(100%-2rem)] overflow-y-auto rounded-xl border border-border bg-card/95 backdrop-blur-sm shadow-2xl">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 backdrop-blur-sm px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: node.color }} />
          <h3 className="font-semibold text-sm truncate">{node.label}</h3>
          <Badge variant="secondary" className="text-[10px] shrink-0">
            {node.columns.length} cols
          </Badge>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {/* Columns */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Columns</p>
          <div className="space-y-0.5">
            {node.columns.map((col) => (
              <div key={col.name} className="flex items-center justify-between py-1 px-2 rounded text-xs hover:bg-accent/50">
                <span className={cn("font-mono", col.name === "id" || col.name.endsWith("_id") ? "text-indigo-400" : "")}>
                  {col.name}
                </span>
                <span className="text-muted-foreground text-[10px]">{col.type}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Relationships */}
        {relatedLinks.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Relationships ({relatedLinks.length})
            </p>
            <div className="space-y-1.5">
              {relatedLinks.map((link, i) => {
                const src = typeof link.source === "string" ? link.source : (link.source as any).id;
                const tgt = typeof link.target === "string" ? link.target : (link.target as any).id;
                const isOutgoing = src === node.id;
                return (
                  <div key={i} className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded bg-accent/30">
                    <span className="font-mono text-indigo-400">{link.sourceColumn}</span>
                    <span className="text-muted-foreground">{isOutgoing ? "\u2192" : "\u2190"}</span>
                    <span className="font-mono font-medium">{isOutgoing ? tgt : src}</span>
                    <span className="text-muted-foreground">.{link.targetColumn}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const DataGraph = () => {
  const { schemaData, isLoading, error, refetch } = useMcpSchema();
  const graphRef = useRef<ForceGraphMethods>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const graphData = useMemo<GraphData>(() => {
    if (!schemaData || schemaData.length === 0) return { nodes: [], links: [] };
    return buildGraphData(schemaData);
  }, [schemaData]);

  // Filter nodes by search
  const filteredData = useMemo<GraphData>(() => {
    if (!searchQuery.trim()) return graphData;
    const q = searchQuery.toLowerCase();
    const matchedIds = new Set(
      graphData.nodes.filter((n) => n.label.toLowerCase().includes(q)).map((n) => n.id),
    );
    return {
      nodes: graphData.nodes.filter((n) => matchedIds.has(n.id)),
      links: graphData.links.filter(
        (l) => matchedIds.has(l.source as string) && matchedIds.has(l.target as string),
      ),
    };
  }, [graphData, searchQuery]);

  const handleNodeClick = useCallback(
    (node: any) => {
      setSelectedNode(node as GraphNode);
      // Center on the node
      graphRef.current?.centerAt(node.x, node.y, 400);
      graphRef.current?.zoom(2, 400);
    },
    [],
  );

  const handleZoomIn = () => graphRef.current?.zoom(2, 300);
  const handleZoomOut = () => graphRef.current?.zoom(0.5, 300);
  const handleFit = () => graphRef.current?.zoomToFit(400, 40);

  // ── Node rendering ─────────────────────────────────────────────────────────

  const nodeCanvasObject = useCallback(
    (node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const n = node as GraphNode;
      const isSelected = selectedNode?.id === n.id;
      const isHovered = hoveredNode?.id === n.id;
      const fontSize = Math.max(10 / globalScale, 2);
      const radius = Math.max(n.val * 1.5, 6);

      // Glow for selected/hovered
      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.arc(node.x!, node.y!, radius + 4, 0, 2 * Math.PI);
        ctx.fillStyle = n.color + "40";
        ctx.fill();
      }

      // Node circle
      ctx.beginPath();
      ctx.arc(node.x!, node.y!, radius, 0, 2 * Math.PI);
      ctx.fillStyle = n.color;
      ctx.fill();
      ctx.strokeStyle = isSelected ? "#fff" : n.color + "80";
      ctx.lineWidth = isSelected ? 2 / globalScale : 1 / globalScale;
      ctx.stroke();

      // Label with background pill for readability
      ctx.font = `${isSelected || isHovered ? "bold " : ""}${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      const labelY = node.y! + radius + 3;
      const textWidth = ctx.measureText(n.label).width;
      const padX = 4 / globalScale;
      const padY = 2 / globalScale;
      ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
      ctx.beginPath();
      ctx.roundRect(
        node.x! - textWidth / 2 - padX,
        labelY - padY,
        textWidth + padX * 2,
        fontSize + padY * 2,
        3 / globalScale,
      );
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.fillText(n.label, node.x!, labelY);

      // Column count badge
      if (globalScale > 0.8) {
        const countText = String(n.columns.length);
        ctx.font = `bold ${Math.max(7 / globalScale, 2)}px Inter, system-ui, sans-serif`;
        const tw = ctx.measureText(countText).width;
        ctx.fillStyle = "rgba(0,0,0,0.6)";
        ctx.beginPath();
        ctx.roundRect(node.x! - tw / 2 - 2, node.y! - 3, tw + 4, 8 / globalScale, 2);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.textBaseline = "middle";
        ctx.fillText(countText, node.x!, node.y!);
      }
    },
    [selectedNode, hoveredNode],
  );

  // ── Link rendering ─────────────────────────────────────────────────────────

  const linkCanvasObject = useCallback(
    (link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const src = link.source;
      const tgt = link.target;
      if (!src || !tgt || typeof src.x !== "number") return;

      const isRelated =
        selectedNode &&
        ((src.id || src) === selectedNode.id || (tgt.id || tgt) === selectedNode.id);

      ctx.beginPath();
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
      ctx.strokeStyle = isRelated ? "#818cf8" : "rgba(100, 116, 139, 0.25)";
      ctx.lineWidth = isRelated ? 1.5 / globalScale : 0.5 / globalScale;
      ctx.stroke();

      // Arrow at midpoint
      if (globalScale > 0.6) {
        const midX = (src.x + tgt.x) / 2;
        const midY = (src.y + tgt.y) / 2;
        const angle = Math.atan2(tgt.y - src.y, tgt.x - src.x);
        const arrowLen = 4 / globalScale;
        ctx.beginPath();
        ctx.moveTo(midX + arrowLen * Math.cos(angle), midY + arrowLen * Math.sin(angle));
        ctx.lineTo(
          midX - arrowLen * Math.cos(angle - Math.PI / 6),
          midY - arrowLen * Math.sin(angle - Math.PI / 6),
        );
        ctx.lineTo(
          midX - arrowLen * Math.cos(angle + Math.PI / 6),
          midY - arrowLen * Math.sin(angle + Math.PI / 6),
        );
        ctx.closePath();
        ctx.fillStyle = isRelated ? "#818cf8" : "rgba(100, 116, 139, 0.4)";
        ctx.fill();
      }

      // Link label
      if (isRelated && globalScale > 1) {
        const midX = (src.x + tgt.x) / 2;
        const midY = (src.y + tgt.y) / 2;
        const fontSize = Math.max(8 / globalScale, 2);
        ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#94a3b8";
        ctx.fillText(link.label, midX, midY - 4 / globalScale);
      }
    },
    [selectedNode],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex flex-1 min-h-0 w-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading schema...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 min-h-0 w-full flex-col items-center justify-center gap-3 text-muted-foreground">
        <p className="text-sm">Failed to load schema</p>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (graphData.nodes.length === 0) {
    return (
      <div className="flex flex-1 min-h-0 w-full flex-col items-center justify-center gap-3 text-muted-foreground">
        <p className="text-sm">No tables found in schema</p>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2 shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold">Data Graph</h2>
          <Badge variant="secondary" className="text-[10px]">
            {filteredData.nodes.length} tables
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {filteredData.links.length} relationships
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search tables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              className="h-7 w-44 pl-7 text-xs"
            />
            {searchQuery && (
              <button
                className="absolute right-1.5 top-1/2 -translate-y-1/2"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={handleZoomIn} title="Zoom In">
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={handleZoomOut} title="Zoom Out">
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={handleFit} title="Fit to View">
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => refetch()}>
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Graph Canvas */}
      <div ref={containerRef} className="relative flex-1 bg-background">
        <ForceGraph2D
          ref={graphRef as any}
          width={dimensions.width}
          height={dimensions.height}
          graphData={filteredData}
          nodeCanvasObject={nodeCanvasObject}
          nodePointerAreaPaint={(node: any, color, ctx) => {
            const r = Math.max((node as GraphNode).val * 1.5, 6) + 4;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(node.x!, node.y!, r, 0, 2 * Math.PI);
            ctx.fill();
          }}
          linkCanvasObject={linkCanvasObject}
          onNodeClick={handleNodeClick}
          onNodeHover={(node) => setHoveredNode(node as GraphNode | null)}
          onBackgroundClick={() => setSelectedNode(null)}
          cooldownTicks={100}
          d3AlphaDecay={0.02}
          d3VelocityDecay={0.3}
          enableNodeDrag={true}
          backgroundColor="transparent"
        />

        {/* Detail Panel */}
        {selectedNode && (
          <DetailPanel
            node={selectedNode}
            links={graphData.links}
            onClose={() => setSelectedNode(null)}
          />
        )}

        {/* Legend */}
        <div className="absolute left-4 bottom-4 z-10 rounded-lg border border-border bg-card/90 backdrop-blur-sm px-3 py-2 text-[10px] text-muted-foreground space-y-1">
          <p className="font-medium">Click a node to see details</p>
          <p>Drag to reposition nodes</p>
          <p>Scroll to zoom, drag background to pan</p>
        </div>
      </div>
    </div>
  );
};

export default DataGraph;
